import { Link } from "react-router-dom";
import { PageFooter } from "../components/PageFooter";

/**
 * Privacy policy page — Apple App Store reviewers reject submissions that
 * declare camera / photo permissions without a public privacy policy URL.
 * This page is intentionally honest, plain-English and short — every claim
 * here is verifiable from the codebase.
 */
const Privacy = () => {
  return (
    <div className="min-h-screen bg-white text-black flex flex-col">
      <header className="border-b border-black px-4 sm:px-6 py-3 flex items-center justify-between text-[11px] tracking-[0.18em] uppercase">
        <Link to="/shop" className="hover:underline">
          ← Shop
        </Link>
        <div className="font-semibold">Privacy</div>
        <Link to="/" className="hover:underline">
          Cover
        </Link>
      </header>

      <main
        data-testid="privacy-page"
        className="px-4 sm:px-6 lg:px-14 max-w-3xl mx-auto py-12 sm:py-16 space-y-8 text-[15px] leading-relaxed text-[#1a1a1a]"
      >
        <div>
          <div className="kb-mono-label kb-blue">PRIVACY · v1.0</div>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2">
            KISUBIKA Privacy Policy
          </h1>
          <p className="text-[var(--kb-fg-2)] mt-2">
            Last updated: May 12, 2026
          </p>
        </div>

        <section className="space-y-3">
          <h2 className="font-serif text-2xl">1. What we collect</h2>
          <p>
            When you reserve a bag, we collect your <strong>name, email,
            optional shipping address</strong> and a <strong>£20 deposit</strong>
            payment record (via Stripe). We never see or store your card
            number — Stripe handles that. When you preview a bag in AR, your
            device’s camera feed stays on your device; we don’t upload, save
            or analyse it.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-serif text-2xl">2. How we use it</h2>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>To hold your reservation and ship your bag.</li>
            <li>To email you order confirmations and shipping updates.</li>
            <li>To prevent fraud and meet our legal obligations.</li>
            <li>To improve KISUBIKA via aggregate, anonymised analytics.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-serif text-2xl">3. Camera & photos</h2>
          <p>
            On iOS we ask for camera permission only when you tap{" "}
            <em>View in AR</em>. The camera feed is rendered locally to show
            the bag in your space. If you tap <em>Share</em>, we generate a
            Story-ready image entirely on your device and offer it to the
            iOS share sheet — KISUBIKA never receives that image.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-serif text-2xl">4. Who sees your data</h2>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>
              <strong>Stripe</strong> (payments) — only what they need to
              process the £20 deposit.
            </li>
            <li>
              <strong>Our shipping carrier</strong> — only your address when
              we ship.
            </li>
            <li>
              <strong>PostHog</strong> (when enabled) — anonymous product
              analytics, no personal identifiers.
            </li>
          </ul>
          <p>We do not sell your data. Ever.</p>
        </section>

        <section className="space-y-3">
          <h2 className="font-serif text-2xl">5. Your rights</h2>
          <p>
            You can request a copy or deletion of your data at any time by
            emailing{" "}
            <a className="underline" href="mailto:privacy@kisubika.app">
              privacy@kisubika.app
            </a>
            . We respond within 30 days, as the UK Data Protection Act 2018
            requires.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-serif text-2xl">6. Contact</h2>
          <p>
            KISUBIKA is operated by Pleasurealm Ltd, London, UK.{" "}
            <a className="underline" href="mailto:hello@kisubika.app">
              hello@kisubika.app
            </a>
          </p>
        </section>
      </main>

      <PageFooter />
    </div>
  );
};

export default Privacy;
